import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getAstroApplications } from '../../api';
import { PageHeader, StatusBadge, Table, Pagination, SearchInput, EmptyState, TableSkeleton } from '../../components/ui';
import OverviewBar from '../../components/ui/OverviewBar';
import { ClipboardList, Eye, RefreshCw, Filter, X, UserCheck } from 'lucide-react';

type AppRow = {
  _id: string;
  applicationId: string;
  name: string;
  phone: string;
  email?: string;
  experience: number;
  specialities: string[];
  status: 'under_review' | 'approved' | 'rejected';
  createdAt: string;
  assignedLoginId?: string;
  isExistingMember?: boolean;
  linkedUser?: { _id: string; name?: string; phone?: string; email?: string } | null;
};

const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

export default function AstrologerApplicationsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('under_review');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['admin-astro-applications', page, search, status, from, to],
    queryFn: async () => {
      const params: any = { page, limit: 20 };
      if (search) params.search = search;
      if (status) params.status = status;
      if (from) params.from = from;
      if (to) params.to = to;
      const r = await getAstroApplications(params);
      return r.data;
    },
    placeholderData: (prev) => prev,
  });

  const rows: AppRow[] = data?.data || [];
  const pagination = data?.pagination;
  const counts = data?.statusCounts || {};

  return (
    <div className="p-6">
      <PageHeader
        title="Astrologer Applications"
        subtitle="Review Become-an-Astrologer applications and issue login credentials"
      >
        <button className="btn-secondary" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} /> Refresh
        </button>
      </PageHeader>

      <OverviewBar
        items={[
          { label: 'Under Review', value: counts.under_review || 0, icon: <ClipboardList size={14} /> },
          { label: 'Approved', value: counts.approved || 0 },
          { label: 'Rejected', value: counts.rejected || 0 },
        ]}
      />

      <div className="card p-4 mb-5 space-y-3">
        <div className="flex flex-wrap gap-2">
          {STATUS_TABS.map((t) => (
            <button
              key={t.value || 'all'}
              onClick={() => { setStatus(t.value); setPage(1); }}
              className={`px-3 py-1.5 rounded-full text-sm border transition ${
                status === t.value
                  ? 'bg-primary-500 text-white border-primary-500'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-primary-300'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[200px]">
            <SearchInput
              value={search}
              onChange={(v) => { setSearch(v); setPage(1); }}
              placeholder="Search name, phone, application ID…"
            />
          </div>
          <button className="btn-secondary" onClick={() => setShowFilters(!showFilters)}>
            <Filter size={14} /> Filters
          </button>
          {(from || to || search) && (
            <button className="btn-secondary" onClick={() => { setFrom(''); setTo(''); setSearch(''); setPage(1); }}>
              <X size={14} /> Clear
            </button>
          )}
        </div>
        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-gray-100">
            <label className="text-sm text-gray-600">
              From date
              <input type="date" className="input mt-1" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
            </label>
            <label className="text-sm text-gray-600">
              To date
              <input type="date" className="input mt-1" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} />
            </label>
          </div>
        )}
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <TableSkeleton rows={8} cols={7} />
        ) : rows.length === 0 ? (
          <EmptyState message="No applications found" icon={<ClipboardList size={32} />} />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <th className="table-th">#</th>
                  <th className="table-th">Application ID</th>
                  <th className="table-th">Name</th>
                  <th className="table-th">Mobile</th>
                  <th className="table-th">Member</th>
                  <th className="table-th">Experience</th>
                  <th className="table-th">Status</th>
                  <th className="table-th">Submitted</th>
                  <th className="table-th text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row._id} className="table-row">
                    <td className="table-td text-gray-400">{(page - 1) * 20 + idx + 1}</td>
                    <td className="table-td font-mono text-xs">{row.applicationId}</td>
                    <td className="table-td font-medium">{row.name}</td>
                    <td className="table-td">{row.phone}</td>
                    <td className="table-td">
                      {row.isExistingMember && row.linkedUser?._id ? (
                        <button
                          type="button"
                          title="Open user details"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/users/${row.linkedUser!._id}`);
                          }}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-sky-100 text-sky-800 hover:bg-sky-200"
                        >
                          <UserCheck size={12} />
                          {row.linkedUser.name || 'Member'}
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">Guest apply</span>
                      )}
                    </td>
                    <td className="table-td">{row.experience} yrs</td>
                    <td className="table-td">
                      <StatusBadge status={row.status === 'under_review' ? 'pending' : row.status} />
                    </td>
                    <td className="table-td text-sm text-gray-500">
                      {new Date(row.createdAt).toLocaleDateString()}
                    </td>
                    <td className="table-td text-right">
                      <button
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-600"
                        onClick={() => navigate(`/astrologers/applications/${row._id}`)}
                        title="View"
                      >
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {pagination?.pages > 1 && (
              <div className="p-4 border-t border-gray-100">
                <Pagination
                  page={pagination.page}
                  pages={pagination.pages}
                  total={pagination.total}
                  limit={20}
                  onPageChange={setPage}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
